import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getSeats } from "../api.js";

type GuestEntry = { name: string; tableNumber: string };
type Status = "loading" | "error" | "empty" | "ready";

export function SeatsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [status, setStatus] = useState<Status>("loading");
  const [eventTitle, setEventTitle] = useState("");
  const [guests, setGuests] = useState<GuestEntry[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!eventId) { setStatus("error"); return; }
    getSeats(eventId)
      .then((res) => {
        setEventTitle(res.eventTitle);
        setGuests(res.guests);
        setStatus(res.guests.length === 0 ? "empty" : "ready");
      })
      .catch(() => setStatus("error"));
  }, [eventId]);

  // Group guests by table, sorted numerically
  const tables = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const g of guests) {
      if (!map.has(g.tableNumber)) map.set(g.tableNumber, []);
      map.get(g.tableNumber)!.push(g.name);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      const na = Number(a), nb = Number(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b);
    });
  }, [guests]);

  const query = search.trim().toLowerCase();

  const filteredTables = useMemo(() => {
    if (!query) return tables;
    return tables.filter(([, names]) =>
      names.some((n) => n.toLowerCase().includes(query))
    );
  }, [tables, query]);

  if (status === "loading") {
    return (
      <main className="grid min-h-dvh place-items-center">
        <div
          className="h-11 w-11 animate-spin rounded-full border-4 border-neutral-200 border-t-accent"
          aria-label="Loading"
        />
      </main>
    );
  }

  if (status === "error") {
    return (
      <main className="grid min-h-dvh place-items-center px-4 text-center">
        <div>
          <p className="font-script text-4xl text-accent mb-2">Oops</p>
          <p className="text-muted">We couldn't load the seating chart. Please try again.</p>
        </div>
      </main>
    );
  }

  if (status === "empty") {
    return (
      <main className="grid min-h-dvh place-items-center px-4 text-center">
        <div>
          <h1 className="font-script text-5xl text-accent mb-3">{eventTitle}</h1>
          <p className="text-muted">Seating assignments aren't available yet. Check back soon!</p>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-lg px-4 py-10">
      <header className="mb-6 text-center">
        <p className="text-xs uppercase tracking-[0.35em] text-accent mb-1">Find Your Seat</p>
        <h1 className="font-script text-5xl leading-tight">{eventTitle}</h1>
      </header>

      {/* Search */}
      <div className="relative mb-6">
        <input
          className="field w-full pr-9"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
        {search && (
          <button
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-ink transition text-lg leading-none"
            onClick={() => setSearch("")}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {/* No results */}
      {filteredTables.length === 0 && (
        <p className="text-center text-muted mt-8">
          No results for "<span className="font-medium text-ink">{search}</span>".
        </p>
      )}

      {/* Table cards */}
      <div className="space-y-4">
        {filteredTables.map(([tableNumber, names]) => (
          <div key={tableNumber} className="card">
            <p className="font-script text-4xl text-accent mb-3">Table {tableNumber}</p>
            <ul className="space-y-2">
              {names.map((name) => {
                const isMatch = query && name.toLowerCase().includes(query);
                return (
                  <li
                    key={name}
                    className={`text-base ${isMatch ? "font-semibold text-accent" : "text-ink"}`}
                  >
                    {name}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </main>
  );
}
